import type { TaxonomyDraft, TaxonomyDraftIssue } from "@sitescene/types";

import { Button } from "@/components/field";

interface DraftBarProps {
	busy: boolean;
	/** What publishing changes, in one line, for the confirmation. */
	confirmMessage: string;
	draft: null | TaxonomyDraft;
	error: Error | null;
	issues: TaxonomyDraftIssue[];
	onDiscard: () => void;
	onPublish: () => void;
	/** Shown after a successful publish. */
	published?: string;
	/** Review rows: one line per staged change. */
	review: { key: string; label: string; onRevert: () => void }[];
}

/**
 * The open draft: what it changes, what blocks publishing it, and the publish
 * and discard actions. Publishing is refused server-side while any issue
 * stands; the button is disabled here so the reason is visible first.
 */
export const DraftBar = ({
	busy,
	confirmMessage,
	draft,
	error,
	issues,
	onDiscard,
	onPublish,
	published,
	review,
}: DraftBarProps) => {
	if (!draft || review.length === 0) {
		return (
			<section className="border-secondary rounded-md border p-3 text-sm">
				<p className="text-secondary">
					No unpublished changes. Edits are saved to a draft and reach the
					verifier only when published.
				</p>
				{published && (
					<p className="text-primary mt-1" role="status">
						{published}
					</p>
				)}
				{error && (
					<p className="text-error-primary mt-1" role="alert">
						{error.message}
					</p>
				)}
			</section>
		);
	}

	return (
		<section className="border-brand-600 rounded-md border p-3 text-sm">
			<div className="flex flex-wrap items-center gap-2">
				<h3 className="text-primary font-semibold">
					Draft: {review.length} change{review.length === 1 ? "" : "s"}
				</h3>
				<span className="text-tertiary text-xs">
					last edited {new Date(draft.updatedAt).toLocaleString()}
				</span>
				<div className="ml-auto flex gap-2">
					<Button
						disabled={busy}
						onClick={() => {
							if (window.confirm("Discard every change in this draft?")) {
								onDiscard();
							}
						}}
						type="button"
						variant="secondary"
					>
						Discard draft
					</Button>
					<Button
						disabled={busy || issues.length > 0}
						onClick={() => {
							if (window.confirm(confirmMessage)) onPublish();
						}}
						type="button"
					>
						{busy ? "Publishing…" : "Publish"}
					</Button>
				</div>
			</div>

			<ul className="mt-2 flex flex-col gap-1">
				{review.map((row) => (
					<li className="flex items-center gap-2" key={row.key}>
						<span className="text-primary">{row.label}</span>
						<button
							className="text-tertiary hover:text-error-primary text-xs underline"
							disabled={busy}
							onClick={row.onRevert}
							type="button"
						>
							undo
						</button>
					</li>
				))}
			</ul>

			{issues.length > 0 && (
				<div className="mt-2" role="alert">
					<p className="text-error-primary font-medium">
						Fix before publishing:
					</p>
					<ul className="text-error-primary list-disc pl-5">
						{issues.map((issue) => (
							<li key={`${issue.target}|${issue.field ?? ""}|${issue.message}`}>
								{issue.kind === "conflict" ? "Conflict — " : ""}
								{issue.message}
							</li>
						))}
					</ul>
				</div>
			)}
			{error && (
				<p className="text-error-primary mt-2" role="alert">
					{error.message}
				</p>
			)}
		</section>
	);
};
